import React, { useState } from "react";
import { Plus, Mic, Upload, Lightbulb } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { CaptureThought } from "../capture/thoughts/CaptureThought";
import { UploadDialog } from "../capture/upload/UploadDialog";
import { RecordingInterface } from "../capture/shared/RecordingInterface";
import { useToast } from "../ui/use-toast";
import { transcribeAudio } from "../../lib/transcription";
import { useAuth } from "../../contexts/AuthContext";

export function FloatingActionButton() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isThoughtModalOpen, setIsThoughtModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleRecordingComplete = async (blob: Blob) => {
    if (!user) return;
    
    try {
      const transcription = await transcribeAudio(blob, user.id, 'meeting');
      toast({
        title: "Recording complete",
        description: "Your recording has been saved and is being transcribed.",
      });
      setIsRecording(false);
    } catch (error) {
      console.error("Transcription error:", error);
      toast({
        title: "Error",
        description: "Failed to process recording. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isRecording) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <RecordingInterface
          onCancel={() => setIsRecording(false)}
          onComplete={handleRecordingComplete}
          title="Quick Recording"
        />
      </div>
    );
  }

  const actionButtonClass = "h-14 w-14 rounded-full bg-amber-500 hover:bg-amber-600 border-2 border-amber-700 dark:bg-amber-900/50 dark:hover:bg-amber-800/50 dark:border-amber-700 text-amber-700 transition-colors relative";
  const iconClass = "h-16 w-16";
  const labelClass = "absolute -bottom-3 left-1/2 -translate-x-1/2 text-xs text-slate-600 font-medium text-muted-foreground/40 whitespace-nowrap";

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50">
        <div 
          className={cn(
            "flex items-center transition-all duration-200 bg-background/40 backdrop-blur-sm rounded-full shadow-lg",
            isExpanded ? "p-4 pl-6" : ""
          )}
          onMouseEnter={() => setIsExpanded(true)}
          onMouseLeave={() => setIsExpanded(false)}
        >
          {isExpanded ? (
            <div className="flex gap-6">
              <div className="relative group">
  <Button
    variant="ghost"
    size="icon"
    className={actionButtonClass}
    onClick={() => setIsRecording(true)}
  >
    <Mic className={iconClass} />
    <span className="sr-only">Record Meeting</span>
  </Button>
  <span className={labelClass}>Record</span>
</div>

<div className="relative group">
  <Button
    variant="ghost"
    size="icon"
    className={actionButtonClass}
    onClick={() => setIsUploadModalOpen(true)}
  >
    <Upload className={iconClass} />
    <span className="sr-only">Upload File</span>
  </Button>
  <span className={labelClass}>Upload</span>
</div>

<div className="relative group">
  <Button
    variant="ghost"
    size="icon"
    className={actionButtonClass}
    onClick={() => setIsThoughtModalOpen(true)}
  >
    <Lightbulb className={iconClass} />
    <span className="sr-only">Add Thought</span>
  </Button>
  <span className={labelClass}>Thought</span>
</div>

              <div className="relative">
                <Button
                  variant="ghost" 
                  size="icon"
                  className="h-16 w-16 rounded-full bg-amber-500 hover:bg-amber-600 border-2 border-amber-700 dark:bg-amber-900/50 dark:hover:bg-amber-800/50 dark:border-amber-700 text-amber-900 transition-transform duration-200 rotate-45"
                  onClick={() => setIsExpanded(false)}
                >
                  <Plus size={48} className="stroke-[2]" /> {/* Use size prop here */}
                  <span className="sr-only">Close quick actions menu</span>
                </Button>
                
              </div>
            </div>
          ) : (
            <div className="relative">
              <Button
                variant="ghost" 
                size="icon"
                className="h-16 w-16 rounded-full bg-amber-500 hover:bg-amber-600 border-2 border-amber-700 dark:bg-amber-900/50 dark:hover:bg-amber-800/50 dark:border-amber-700 text-amber-900 transition-transform duration-200"
                onClick={() => setIsExpanded(true)}
              >
                <Plus className="h-12 w-12 stroke-[2]" />
                <span className="sr-only">Open quick actions menu</span>
              </Button>
              
            </div>
          )}
        </div>
      </div>

      <CaptureThought
        isTyping={false}
        setIsTyping={() => {}}
        open={isThoughtModalOpen}
        onOpenChange={setIsThoughtModalOpen}
      />

      <UploadDialog
        open={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
      />
    </>
  );
}